import { useEffect } from "react";
import { ToastContainer } from "react-toastify";

import { useAppSelector, useAppDispatch } from "../services/hooks";
import { clearErrors } from "../services/errorServices/errorAction";
import { clearAppMessage } from "../services/appMessageServices/appMessageActions";
import NotificationAlertOptions from "./Alerts";
import getErrorMessage, { hasDisplayableError } from "../utils/getErrorMessage";

/**
 * Dedicated react-toastify container id for the shell-level notification hub. A
 * distinct id keeps these toasts off the default container, so the many existing
 * direct `NotificationAlertOptions` calls (which target the default container)
 * are never doubled — react-toastify routes each toast to a single container by
 * id.
 */
export const GLOBAL_NOTIFICATION_CONTAINER_ID = "global-notifications";

/**
 * Fixed toast id so a burst of global errors updates one toast instead of
 * stacking a fresh one for each.
 */
export const GLOBAL_ERROR_TOAST_ID = "global-error-toast";

/**
 * Fixed toast id for app-level success/info messages — kept distinct from the
 * error toast so a success and an error can coexist rather than replace one
 * another.
 */
export const GLOBAL_APP_MESSAGE_TOAST_ID = "global-app-message-toast";

/**
 * SCRUM-857/883 — shell-level global notification hub. Mounted once by the
 * authenticated back-office shell (`AdminContainer`) — access is gated per route
 * in `App.jsx` on authentication, not on role.
 *
 * It is the SOLE consumer of both notification slices:
 *
 *   - `state.errors` (SCRUM-857): many thunks dispatch `GET_ERRORS`, but only
 *     some containers surface it, and those usually only handle the
 *     `{ errors: [{ msg }] }` shape — a bare `{ message }` / `{ msg }` / string
 *     error is shown nowhere. This raises a toast for any displayable error the
 *     local handlers miss, then clears the slice.
 *   - `state.appMessage` (SCRUM-883): the ready-to-show `{ type, message }`
 *     success/info channel. A thunk publishes the final text; this toasts it and
 *     clears the slice — replacing the old "sentinel round-trip" where each
 *     container translated a sentinel string and toasted itself.
 *
 * It owns its render surface: the `<ToastContainer>` it returns is scoped to
 * `GLOBAL_NOTIFICATION_CONTAINER_ID`, so mounting this one component is all a
 * shell needs, and the existing default-container toasts are left untouched.
 *
 * NOTE (SCRUM-883): a container that must do more than toast on a success (e.g.
 * reset a form) must drive that side-effect from its OWN signal — the thunk's
 * resolved promise or a domain success action — never by reading/clearing this
 * shared slice. Otherwise the child-before-parent effect order races this hub's
 * clear and the toast silently drops.
 */
const GlobalNotifications = () => {
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s) => s.errors);
  const appMessage = useAppSelector((s) => s.appMessage as any);

  useEffect(() => {
    if (!hasDisplayableError(errors)) return;

    NotificationAlertOptions("error", getErrorMessage(errors), {
      containerId: GLOBAL_NOTIFICATION_CONTAINER_ID,
      toastId: GLOBAL_ERROR_TOAST_ID,
    });
    dispatch(clearErrors());
  }, [errors, dispatch]);

  useEffect(() => {
    if (!appMessage || !appMessage.message) return;

    NotificationAlertOptions(appMessage.type, appMessage.message, {
      containerId: GLOBAL_NOTIFICATION_CONTAINER_ID,
      toastId: GLOBAL_APP_MESSAGE_TOAST_ID,
    });
    dispatch(clearAppMessage());
  }, [appMessage, dispatch]);

  return <ToastContainer containerId={GLOBAL_NOTIFICATION_CONTAINER_ID} />;
};

export default GlobalNotifications;
